export type OffsetDirection = 'BEFORE' | 'AFTER';

export type EventTypeDefinition = {
  eventType: string;
  supportedDirections: OffsetDirection[];
  minOffsetMinutes: { BEFORE?: number; AFTER?: number };
};

export type CommunicationDictionary = {
  eventTypes: string[];
  channels: string[];
  eventTypeDefinitions: EventTypeDefinition[];
};

export type CommunicationRule = {
  '@id': string;
  id: string;
  eventType: string;
  channel: string;
  offsetMinutes: number;
  direction: OffsetDirection;
  template: string | null;
  active: boolean;
  lastScannedAt: string | null;
};

export type CommunicationRuleRow = {
  key: string;
  '@id'?: string;
  eventType: string;
  channel: string;
  offsetMinutes: number;
  direction: OffsetDirection | '';
  template: string;
  active: boolean;
  lastScannedAt: string | null;
  savedActive: boolean | null;
  savedTemplate: string | null;
};

export type RuleValidationError = {
  translationKey: string;
  params?: { [key: string]: string };
};

export type SelectOption = { value: string; label: string };

export type OffsetParts = { days: number; hours: number; minutes: number };

export const MAX_OFFSET_DAYS = 31;

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

export const splitOffset = (offsetMinutes: number): OffsetParts => ({
  days: Math.floor(offsetMinutes / MINUTES_IN_DAY),
  hours: Math.floor((offsetMinutes % MINUTES_IN_DAY) / MINUTES_IN_HOUR),
  minutes: offsetMinutes % MINUTES_IN_HOUR,
});

export const joinOffset = ({ days, hours, minutes }: OffsetParts): number =>
  days * MINUTES_IN_DAY + hours * MINUTES_IN_HOUR + minutes;

export const createRangeOptions = (
  max: number,
  currentValue: number
): SelectOption[] =>
  Array.from({ length: Math.max(max, currentValue) + 1 }, (_, value) => ({
    value: String(value),
    label: String(value),
  }));

export const withCurrentValueOption = (
  options: SelectOption[],
  value: string
): SelectOption[] =>
  value === '' || options.some(option => option.value === value)
    ? options
    : [...options, { value, label: value }];

export const findDefinition = (
  definitions: EventTypeDefinition[],
  eventType: string
): EventTypeDefinition | undefined =>
  definitions.find(definition => definition.eventType === eventType);

export const getDirectionsForEventType = (
  definitions: EventTypeDefinition[],
  eventType: string
): OffsetDirection[] =>
  findDefinition(definitions, eventType)?.supportedDirections ?? [];

export const getDefaultDirection = (
  definitions: EventTypeDefinition[],
  eventType: string
): OffsetDirection | '' => {
  const directions = getDirectionsForEventType(definitions, eventType);

  return directions.length === 1 && directions[0] ? directions[0] : '';
};

export const ruleToRow = (rule: CommunicationRule): CommunicationRuleRow => ({
  key: rule['@id'],
  '@id': rule['@id'],
  eventType: rule.eventType,
  channel: rule.channel,
  offsetMinutes: rule.offsetMinutes,
  direction: rule.direction,
  template: rule.template ?? '',
  active: rule.active,
  lastScannedAt: rule.lastScannedAt,
  savedActive: rule.active,
  savedTemplate: rule.template ?? '',
});

export const createEmptyRow = (
  key: string,
  channels: string[]
): CommunicationRuleRow => ({
  key,
  eventType: '',
  channel: channels.length === 1 && channels[0] ? channels[0] : '',
  offsetMinutes: 0,
  direction: '',
  template: '',
  active: true,
  lastScannedAt: null,
  savedActive: null,
  savedTemplate: null,
});

export const hasUnsavedChanges = (row: CommunicationRuleRow): boolean =>
  !row['@id'] ||
  row.active !== row.savedActive ||
  row.template !== row.savedTemplate;

export const validateRow = (
  row: CommunicationRuleRow,
  definitions: EventTypeDefinition[]
): RuleValidationError | null => {
  if (!row.eventType || !row.channel || !row.direction) {
    return { translationKey: 'communicationRules.validation.required' };
  }

  const definition = findDefinition(definitions, row.eventType);

  if (!definition?.supportedDirections.includes(row.direction)) {
    return {
      translationKey: 'communicationRules.validation.unsupportedDirection',
    };
  }

  const minOffsetMinutes = definition.minOffsetMinutes[row.direction] ?? 1;

  if (row.offsetMinutes < minOffsetMinutes) {
    return {
      translationKey: 'communicationRules.validation.offsetTooShort',
      params: { minutes: String(minOffsetMinutes) },
    };
  }

  return null;
};

export const isErrorWithoutResponse = (error: unknown): boolean =>
  !(error as { response?: unknown } | null)?.response;

const templateToPayload = (template: string): string | null =>
  template === '' ? null : template;

export const rowToPayload = (row: CommunicationRuleRow) => ({
  eventType: row.eventType,
  channel: row.channel,
  offsetMinutes: row.offsetMinutes,
  direction: row.direction,
  template: templateToPayload(row.template),
  active: row.active,
});

export const rowToUpdatePayload = (row: CommunicationRuleRow) => ({
  active: row.active,
  template: templateToPayload(row.template),
});
